import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Grid, Header, Button, Icon, Table, Label, Modal, Segment,
  Loader, Dimmer, Form, Input, Dropdown, Confirm
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';
import { isRoot } from '../../helpers/utils';

const TierCard = ({ tier, onEdit, onDelete, onSync, onManageMembers }) => {
  const hasFallback = tier.fallback_tier && tier.fallback_tier !== '';
  return (
    <Card fluid>
      <Card.Content>
        <Card.Header>
          <Icon name='layer group' />
          {tier.display_name || tier.tier_name}
          <Label
            color={tier.online_count > 0 ? 'green' : 'grey'}
            size='mini'
            style={{ marginLeft: 8 }}
          >
            {tier.online_count}/{tier.total_count} 在线
          </Label>
          {tier.status === 0 && (
            <Label color='red' size='mini' style={{ marginLeft: 4 }}>已禁用</Label>
          )}
        </Card.Header>
        <Card.Meta>
          虚拟模型名: <code>{tier.tier_name}</code>
          {hasFallback && (
            <span style={{ marginLeft: 12 }}>
              <Icon name='arrow down' size='small' />
              降级到: <code>{tier.fallback_tier}</code>
            </span>
          )}
        </Card.Meta>
        {tier.description && <Card.Description>{tier.description}</Card.Description>}
      </Card.Content>
      <Card.Content extra>
        <Button size='mini' basic onClick={() => onManageMembers(tier)}>
          <Icon name='list' /> 管理成员
        </Button>
        {isRoot() && (
          <>
            <Button size='mini' basic color='blue' onClick={() => onSync(tier.id)}>
              <Icon name='sync' /> 同步
            </Button>
            <Button size='mini' basic onClick={() => onEdit(tier)}>
              <Icon name='edit' /> 编辑
            </Button>
            <Button size='mini' basic color='red' onClick={() => onDelete(tier)}>
              <Icon name='trash' /> 删除
            </Button>
          </>
        )}
      </Card.Content>
    </Card>
  );
};

const TierFormModal = ({ open, onClose, onSave, tier, allTiers }) => {
  const [form, setForm] = useState({
    tier_name: '', display_name: '', description: '', fallback_tier: '', priority: 0, status: 1
  });
  const isEdit = tier && tier.id;

  useEffect(() => {
    if (tier) {
      setForm({
        tier_name: tier.tier_name || '',
        display_name: tier.display_name || '',
        description: tier.description || '',
        fallback_tier: tier.fallback_tier || '',
        priority: tier.priority || 0,
        status: tier.status !== undefined ? tier.status : 1,
      });
    } else {
      setForm({ tier_name: '', display_name: '', description: '', fallback_tier: '', priority: 0, status: 1 });
    }
  }, [tier, open]);

  const fallbackOptions = [
    { key: 'none', text: '(无降级)', value: '' },
    ...allTiers
      .filter(t => !isEdit || t.id !== tier.id)
      .map(t => ({ key: t.id, text: t.display_name || t.tier_name, value: t.tier_name }))
  ];

  const handleSubmit = () => {
    if (!form.tier_name) {
      showError('档位名称不能为空');
      return;
    }
    onSave(form, isEdit ? tier.id : null);
  };

  return (
    <Modal open={open} onClose={onClose} size='small'>
      <Modal.Header>{isEdit ? '编辑档位' : '新建档位'}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field>
            <label>档位名称 (虚拟模型名)</label>
            <Input
              value={form.tier_name}
              onChange={(e, { value }) => setForm({ ...form, tier_name: value })}
              placeholder='如: tier-high'
              disabled={isEdit}
            />
          </Form.Field>
          <Form.Field>
            <label>显示名称</label>
            <Input
              value={form.display_name}
              onChange={(e, { value }) => setForm({ ...form, display_name: value })}
              placeholder='如: 高档模型'
            />
          </Form.Field>
          <Form.Field>
            <label>描述</label>
            <Input
              value={form.description}
              onChange={(e, { value }) => setForm({ ...form, description: value })}
            />
          </Form.Field>
          <Form.Field>
            <label>降级档位</label>
            <Dropdown
              selection
              options={fallbackOptions}
              value={form.fallback_tier}
              onChange={(e, { value }) => setForm({ ...form, fallback_tier: value })}
            />
          </Form.Field>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>优先级</label>
              <Input
                type='number'
                value={form.priority}
                onChange={(e, { value }) => setForm({ ...form, priority: parseInt(value) || 0 })}
              />
            </Form.Field>
            <Form.Field>
              <label>状态</label>
              <Dropdown
                selection
                options={[
                  { key: 1, text: '启用', value: 1 },
                  { key: 0, text: '禁用', value: 0 },
                ]}
                value={form.status}
                onChange={(e, { value }) => setForm({ ...form, status: value })}
              />
            </Form.Field>
          </Form.Group>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>取消</Button>
        <Button primary onClick={handleSubmit}>保存</Button>
      </Modal.Actions>
    </Modal>
  );
};

const MembersModal = ({ open, onClose, tier, allModels, onReload }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addForm, setAddForm] = useState({ model_registry_id: '', weight: 1, priority: 0 });

  const loadMembers = useCallback(async () => {
    if (!tier) return;
    setLoading(true);
    try {
      const res = await API.get(`/api/tiers/${tier.id}`);
      const { success, data } = res.data;
      if (success && data) {
        setMembers(data.members || []);
      }
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  }, [tier]);

  useEffect(() => {
    if (open) loadMembers();
  }, [open, loadMembers]);

  const handleAddMember = async () => {
    if (!addForm.model_registry_id) {
      showError('请选择模型');
      return;
    }
    try {
      const res = await API.post(`/api/tiers/${tier.id}/members`, {
        model_registry_id: parseInt(addForm.model_registry_id),
        weight: addForm.weight,
        priority: addForm.priority,
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess('成员已添加');
        loadMembers();
        onReload();
        setAddForm({ model_registry_id: '', weight: 1, priority: 0 });
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      const res = await API.delete(`/api/tiers/${tier.id}/members/${memberId}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('成员已移除');
        loadMembers();
        onReload();
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const modelOptions = allModels.map(m => ({
    key: m.id,
    text: `${m.display_name || m.model_name} (ID: ${m.id})`,
    value: m.id,
  }));

  return (
    <Modal open={open} onClose={onClose} size='large'>
      <Modal.Header>
        <Icon name='users' /> 档位成员 - {tier ? (tier.display_name || tier.tier_name) : ''}
      </Modal.Header>
      <Modal.Content>
        {loading ? (
          <Dimmer active inverted><Loader /></Dimmer>
        ) : (
          <>
            <Table compact celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>模型名称</Table.HeaderCell>
                  <Table.HeaderCell>显示名</Table.HeaderCell>
                  <Table.HeaderCell>权重</Table.HeaderCell>
                  <Table.HeaderCell>优先级</Table.HeaderCell>
                  <Table.HeaderCell>模型状态</Table.HeaderCell>
                  <Table.HeaderCell>操作</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {members.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan='6' textAlign='center'>暂无成员</Table.Cell>
                  </Table.Row>
                ) : (
                  members.map(m => (
                    <Table.Row key={m.id}>
                      <Table.Cell>{m.model_name}</Table.Cell>
                      <Table.Cell>{m.display_name}</Table.Cell>
                      <Table.Cell>{m.weight}</Table.Cell>
                      <Table.Cell>{m.priority}</Table.Cell>
                      <Table.Cell>
                        <Label
                          color={m.model_status === 2 ? 'green' : 'grey'}
                          size='small'
                        >
                          {m.model_status === 2 ? '运行中' : '离线'}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        {isRoot() && (
                          <Button
                            size='mini'
                            color='red'
                            icon='trash'
                            onClick={() => handleRemoveMember(m.id)}
                          />
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>

            {isRoot() && (
              <Segment>
                <Header as='h5'>添加成员</Header>
                <Form>
                  <Form.Group inline>
                    <Form.Field>
                      <Dropdown
                        selection
                        search
                        placeholder='选择模型'
                        options={modelOptions}
                        value={addForm.model_registry_id}
                        onChange={(e, { value }) => setAddForm({ ...addForm, model_registry_id: value })}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Input
                        type='number'
                        placeholder='权重'
                        value={addForm.weight}
                        onChange={(e, { value }) => setAddForm({ ...addForm, weight: parseInt(value) || 1 })}
                        style={{ width: 80 }}
                        label='权重'
                      />
                    </Form.Field>
                    <Form.Field>
                      <Input
                        type='number'
                        placeholder='优先级'
                        value={addForm.priority}
                        onChange={(e, { value }) => setAddForm({ ...addForm, priority: parseInt(value) || 0 })}
                        style={{ width: 80 }}
                        label='优先级'
                      />
                    </Form.Field>
                    <Form.Field style={{ marginLeft: 8 }}>
                      <Button primary onClick={handleAddMember}>
                        <Icon name='plus' /> 添加
                      </Button>
                    </Form.Field>
                  </Form.Group>
                </Form>
              </Segment>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>关闭</Button>
      </Modal.Actions>
    </Modal>
  );
};

const TierManager = () => {
  const [tiers, setTiers] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formModal, setFormModal] = useState({ open: false, tier: null });
  const [membersModal, setMembersModal] = useState({ open: false, tier: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, tier: null });

  const loadTiers = useCallback(async () => {
    try {
      const res = await API.get('/api/tiers');
      const { success, data } = res.data;
      if (success) setTiers(data || []);
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  }, []);

  const loadModels = useCallback(async () => {
    try {
      const res = await API.get('/api/model-manager/models');
      const { success, data } = res.data;
      if (success) setAllModels(data || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadTiers();
    loadModels();
  }, [loadTiers, loadModels]);

  const handleSaveTier = async (form, tierId) => {
    try {
      let res;
      if (tierId) {
        res = await API.put(`/api/tiers/${tierId}`, form);
      } else {
        res = await API.post('/api/tiers', form);
      }
      const { success, message } = res.data;
      if (success) {
        showSuccess(tierId ? '档位已更新' : '档位已创建');
        setFormModal({ open: false, tier: null });
        loadTiers();
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const handleDeleteTier = async () => {
    const tier = deleteConfirm.tier;
    if (!tier) return;
    try {
      const res = await API.delete(`/api/tiers/${tier.id}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('档位已删除');
        loadTiers();
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setDeleteConfirm({ open: false, tier: null });
  };

  const handleSync = async (tierId) => {
    try {
      const res = await API.post(`/api/tiers/${tierId}/sync`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('同步完成');
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            <Icon name='layer group' /> 分档路由管理
            <Button.Group floated='right' size='mini'>
              <Button icon='refresh' onClick={loadTiers} />
              {isRoot() && (
                <Button primary onClick={() => setFormModal({ open: true, tier: null })}>
                  <Icon name='plus' /> 新建档位
                </Button>
              )}
            </Button.Group>
          </Card.Header>
        </Card.Content>
        <Card.Content>
          {loading ? (
            <Dimmer active inverted><Loader content='加载档位列表...' /></Dimmer>
          ) : tiers.length === 0 ? (
            <Segment placeholder textAlign='center'>
              <Header icon>
                <Icon name='layer group' />
                暂无档位配置
              </Header>
              <p>点击"新建档位"创建第一个档位</p>
            </Segment>
          ) : (
            <Grid columns={tiers.length <= 3 ? tiers.length : 3} stackable>
              {tiers.map(tier => (
                <Grid.Column key={tier.id}>
                  <TierCard
                    tier={tier}
                    onEdit={(t) => setFormModal({ open: true, tier: t })}
                    onDelete={(t) => setDeleteConfirm({ open: true, tier: t })}
                    onSync={handleSync}
                    onManageMembers={(t) => setMembersModal({ open: true, tier: t })}
                  />
                </Grid.Column>
              ))}
            </Grid>
          )}
        </Card.Content>
      </Card>

      {/* Fallback Chain Visualization */}
      {tiers.length > 0 && (
        <Card fluid className='chart-card' style={{ marginTop: 16 }}>
          <Card.Content>
            <Card.Header className='header'>
              <Icon name='arrow down' /> 降级链
            </Card.Header>
          </Card.Content>
          <Card.Content>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              {tiers.filter(t => t.fallback_tier).map(tier => (
                <Segment key={tier.id} compact style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Label color={tier.online_count > 0 ? 'green' : 'grey'}>
                    {tier.display_name || tier.tier_name}
                  </Label>
                  <Icon name='arrow right' />
                  <Label color='blue' basic>
                    {tier.fallback_tier}
                  </Label>
                </Segment>
              ))}
              {tiers.filter(t => t.fallback_tier).length === 0 && (
                <span style={{ color: '#999' }}>暂无降级链配置</span>
              )}
            </div>
          </Card.Content>
        </Card>
      )}

      <TierFormModal
        open={formModal.open}
        onClose={() => setFormModal({ open: false, tier: null })}
        onSave={handleSaveTier}
        tier={formModal.tier}
        allTiers={tiers}
      />

      <MembersModal
        open={membersModal.open}
        onClose={() => setMembersModal({ open: false, tier: null })}
        tier={membersModal.tier}
        allModels={allModels}
        onReload={loadTiers}
      />

      <Confirm
        open={deleteConfirm.open}
        header='确认删除档位'
        content={`确定要删除档位 "${deleteConfirm.tier ? (deleteConfirm.tier.display_name || deleteConfirm.tier.tier_name) : ''}" 吗？这将同时删除所有成员关系并清理渠道映射。`}
        confirmButton='删除'
        cancelButton='取消'
        onCancel={() => setDeleteConfirm({ open: false, tier: null })}
        onConfirm={handleDeleteTier}
      />
    </div>
  );
};

export default TierManager;
